import React from "react";
import type { SanityDesignVersion } from "@ouster/shared";

/**
 * Page-level design system version, from the route's `designVersion` field.
 *
 * Provided once per route by `SanityRouteView`, so the value always comes from
 * page props and the server and client renders agree. Never derive it from
 * browser-only state (cookies, storage, media queries): that would break hydration.
 * Without a provider, or without a value, the page is "2" and renders exactly as before.
 */
const DesignVersionContext = React.createContext<SanityDesignVersion>("2");

export function DesignVersionProvider({
  value,
  children,
}: {
  value?: SanityDesignVersion | null;
  children: React.ReactNode;
}) {
  const version = value === "3" ? "3" : "2";

  // iOS Safari only applies `:active` on touch when the page listens for touchstart; the 3.0
  // pressed states (buttons, carets, rows) need it. A passive no-op, on 3.0 pages only.
  React.useEffect(() => {
    if (version !== "3") return;
    const noop = () => undefined;
    document.addEventListener("touchstart", noop, { passive: true });
    return () => document.removeEventListener("touchstart", noop);
  }, [version]);

  return <DesignVersionContext.Provider value={version}>{children}</DesignVersionContext.Provider>;
}

export function useDesignVersion() {
  return React.useContext(DesignVersionContext);
}

export function useIsDesignV3() {
  return useDesignVersion() === "3";
}
